import { get } from 'svelte/store';
import { readDeviceStatus, readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
import { format } from 'svelte-i18n';
import { devices } from 'src/stores/lists/devices';
import { devices_config } from '@devices/index';
import { macToID } from 'src/helpers/interface_helpers';
import { getBthomeDeviceModel } from 'src/helpers/virtual_helpers/virtual_bthome_helper';
import type { VirtualComponentInterface } from 'src/types/VirtualComponents';

/**
 * Builds a human‑readable label for a paired BTHome remote.
 *
 * Resolution order:
 * 1. If the physical device is present in the `devices` store, use its `name`.
 * 2. Otherwise use the remote settings `name` (if any).
 * 3. Otherwise use the BTHome model `name` or, as a fallback, a capitalized `deviceType`.
 * 4. Finally, fall back to a generic "BTHome device ({id})" label.
 *
 * When an address is available, it is appended to the label as "<name> - <addr>".
 *
 * @param {string} deviceID - ID of the main device
 * @param {string} remoteId - Remote component ID
 * @param {Record<string, any>} config - Remote configuration object
 * @param {string | null | undefined} providedAddr - Optional MAC address to use
 * @returns {string} Human‑readable label for the remote
 */
export const getFallbackRemoteLabel = (
	deviceID: string,
	remoteId: string,
	config: Record<string, any>,
	providedAddr?: string | null | undefined,
): string => {
	const settingsMap = (readDeviceSettings(deviceID) ?? {}) as Record<string, any>;
	const statusMap = (readDeviceStatus(deviceID) ?? {}) as Record<string, any>;
	const hasConfig = config && Object.keys(config).length > 0;
	const remoteSettings = (hasConfig ? config : settingsMap?.[remoteId]) || {};
	const remoteStatusRecord = statusMap?.[remoteId] as Record<string, any>;
	const remoteStatus = remoteStatusRecord as VirtualComponentInterface['status'];
	const model = getBthomeDeviceModel(remoteStatus);
	let addr =
		providedAddr ||
		remoteSettings?.addr ||
		remoteStatusRecord?._attrs?.addr ||
		remoteStatusRecord?.addr ||
		config?.addr ||
		config?._attrs?.addr;
	const devicesMap = get(devices);
	let remoteDevice: Record<string, any> | null = null;

	if (addr && devicesMap) {
		remoteDevice = (devicesMap[macToID(addr)] as Record<string, any> | undefined) || null;
	}

	if (!addr && remoteDevice) {
		addr = remoteDevice.addr || remoteDevice.mac;
	}

	const formattedDefault = get(format)('text_virtual_bthome_device', {
		values: { id: remoteSettings?.id ?? remoteId.split(':')?.[1] ?? '' },
		default: 'BTHome device ({id})',
	});

	if (remoteDevice) {
		const deviceName = remoteDevice.name || formattedDefault;
		const label = addr ? `${deviceName} - ${addr}` : deviceName;
		return label;
	}

	const deviceType: string | undefined =
		(remoteDevice as any)?.type ||
		(remoteSettings as Record<string, any>)?.type ||
		(remoteStatusRecord as Record<string, any>)?.type ||
		model?.type;
	const friendlyName =
		(deviceType && devices_config?.[deviceType]?.friendly_name) ||
		deviceType ||
		model?.name ||
		formattedDefault;

	return addr ? `${friendlyName} - ${addr}` : friendlyName;
};

/**
 * Builds a list of fallback paired devices using only device settings.
 *
 * @param {string} deviceID - ID of the main device
 * @returns {Array<Record<string, any>>} Fallback list of paired device detail entries
 */
const CONTROL_REGEX = /^bthomecontrol:2\d{2}$/;
const DEVICE_REGEX = /^bthomedevice:2\d{2}$/;

/**
 * Builds a list of fallback paired devices using only device settings.
 * Prioritizes bthomecontrol entries, then adds bthomedevice entries that don't have a matching control.
 *
 * @param {string} deviceID - ID of the main device
 * @returns {Array<Record<string, any>>} Fallback list of paired device detail entries
 */
export const buildFallbackPairedDevices = (deviceID: string): Array<Record<string, any>> => {
	const settingsMap = (readDeviceSettings(deviceID) ?? {}) as Record<string, any>;
	const fallbackDetails: Array<Record<string, any>> = [];
	const controlByAddr = new Map<string, string>();

	for (const [key, cfg] of Object.entries(settingsMap)) {
		if (!cfg || typeof cfg !== 'object') {
			continue;
		}

		const config = cfg as Record<string, any>;
		const addr = config?.addr;

		if (CONTROL_REGEX.test(key) && addr) {
			controlByAddr.set(addr, key);
			fallbackDetails.push({ [key]: {} });
		} else if (DEVICE_REGEX.test(key)) {
			if (!addr || !controlByAddr.has(addr)) {
				fallbackDetails.push({ [key]: {} });
			}
		}
	}

	return fallbackDetails;
};

/**
 * Gets the localized label for a BTHome remote channel based on object ID and index.
 *
 * @param {number} objId - BTHome object ID (58=button, 60=dimmer, 63=rotation, 96=channel key)
 * @param {number} idx - Channel index
 * @returns {string | null} Localized channel label or null if not recognized
 */
export const getChannelLabel = (objId: number, idx: number): string | null => {
	if (objId === 58) {
		if (idx === 0) {
			return get(format)('remote_channel_left_button', { default: 'Left button' });
		}
		if (idx === 1) {
			return get(format)('remote_channel_right_button', { default: 'Right button' });
		}
		return get(format)('remote_channel_button', {
			values: { index: idx },
			default: `Button ${idx}`,
		});
	}

	if (objId === 60) {
		return get(format)('remote_channel_scroll', { default: 'Scroll' });
	}

	if (objId === 63) {
		const rotationLabels = [
			get(format)('remote_channel_id', { default: 'Channel id' }),
			get(format)('remote_channel_addr', { default: 'Channel addr' }),
			get(format)('remote_channel_name', { default: 'Channel name' }),
		];
		return rotationLabels[idx] || rotationLabels[0];
	}

	if (objId === 96) {
		return get(format)('remote_channel_key', { default: 'Channel key' });
	}

	return null;
};

/**
 * Counts the number of action mappings in a remote configuration.
 * Iterates through all channels and counts events with valid action strings.
 *
 * @param {Record<string, any>} config - Remote configuration object
 * @returns {number} Total number of mapped actions
 */
export const countMappings = (config: Record<string, any>): number => {
	if (!config) {
		return 0;
	}
	return Object.values(config).reduce((channelTotal, channelEvents) => {
		if (!channelEvents) {
			return channelTotal;
		}
		const eventCount = Object.values(channelEvents).filter((eventCfg) => {
			const typedCfg = eventCfg as Record<string, any>;
			return Boolean(typedCfg && typeof typedCfg.action === 'string' && typedCfg.action);
		}).length;
		return channelTotal + eventCount;
	}, 0);
};

/**
 * Deep clones a value using structuredClone or JSON serialization fallback.
 *
 * @template T
 * @param {T} value - Value to clone
 * @returns {T} Cloned value
 */
export const cloneValue = <T>(value: T): T => {
	if (!value) {
		return value;
	}
	// @ts-ignore structuredClone might not be defined in TS lib
	if (typeof structuredClone === 'function') {
		try {
			// @ts-ignore structuredClone might not exist at runtime on older browsers
			return structuredClone(value);
		} catch {
			// fall through to JSON clone
		}
	}
	try {
		return JSON.parse(JSON.stringify(value));
	} catch {
		return value;
	}
};

/**
 * Clones an array of input configurations, filtering out null/invalid entries.
 *
 * @param {Array<Record<string, any>>} inputs - Array of input configurations to clone
 * @returns {Array<Record<string, any>>} Cloned and filtered array
 */
export const cloneInputsArray = (inputs: Array<Record<string, any>>): Array<Record<string, any>> => {
	if (!Array.isArray(inputs)) {
		return [];
	}
	return inputs
		.map((entry) => (entry ? cloneValue(entry) : null))
		.filter((entry): entry is Record<string, any> => Boolean(entry));
};

/**
 * Type guard to check if a config object has entries.
 *
 * @param {Record<string, any> | undefined} config - Configuration object to check
 * @returns {boolean} True if config exists and has keys
 */
export const hasConfigEntries = (config?: Record<string, any>): config is Record<string, any> => {
	return Boolean(config) && Object.keys(config).length > 0;
};

/**
 * Clones a configuration object if it has entries, otherwise returns an empty object.
 *
 * @param {Record<string, any> | undefined} config - Configuration to clone
 * @returns {Record<string, any>} Cloned configuration or empty object
 */
export const cloneConfig = (config?: Record<string, any>): Record<string, any> => {
	if (!hasConfigEntries(config)) {
		return {};
	}
	return { ...config };
};
